import { allBuiltInFunctions, JSONataFunctionDocumentation } from "@stedi/transformers-jsonata";
import type { IRange, languages } from "monaco-editor";

const functionDocsToCompletionItem = (doc: JSONataFunctionDocumentation, range: IRange): languages.CompletionItem => {
  return {
    label: doc.label,
    kind: 1,
    // kind: languages.CompletionItemKind.Function,
    detail: doc.detail,
    documentation: {
      value: doc.documentation,
      isTrusted: true,
    },
    insertText: doc.insertText,
    insertTextRules: 4,
    // insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
    range,
  };
};

const builtInFunctionNames = allBuiltInFunctions.map((jsonataAutocompleteEntry) => {
  return jsonataAutocompleteEntry.label;
});

const generateFunctionsAutocompleteSuggestions = (range: IRange): languages.CompletionItem[] => {
  return allBuiltInFunctions.map((builtInFunctionDocumentation) => {
    return {
      ...functionDocsToCompletionItem(builtInFunctionDocumentation, range),
      // We prefix every function name to make sure that JSONata functions
      // autocomplete items appear _after_ source document autocomplete items.
      // 駾 - one of the last characters in UTF-8
      sortText: `駾-${builtInFunctionDocumentation.label}`,
    };
  });
};

const generateFunctionsSignatureHelp = (textUntilPosition: string): languages.SignatureHelp | undefined => {
  let matchingFunction: JSONataFunctionDocumentation | undefined;
  let matchingSubstring: string | undefined;
  for (const JSONataFunction of allBuiltInFunctions) {
    const functionNameForRegExp = sanitizeRegExpParameter(JSONataFunction.label);

    // this regexp searches for a function on the current line which is not yet closed prior to the current cursor position
    const regexp = new RegExp(`${functionNameForRegExp}\\([^)$]*$`);
    const match = textUntilPosition.match(regexp);

    if (match) {
      matchingFunction = JSONataFunction;
      matchingSubstring = match[0];
      break;
    }
  }

  if (!matchingFunction || !matchingSubstring) {
    return;
  }

  const numberOfCommas = matchingSubstring.split(",").length - 1;

  const signature: languages.SignatureInformation = {
    label: matchingFunction.detail,
    documentation: {
      // prefixing with a line divider to separate parameter docs from the function docs visually
      value: `----------\n\n${matchingFunction.documentation}`,
      isTrusted: true,
    },
    parameters: matchingFunction.parameters.map((parameter) => ({
      label: parameter.label,
      documentation: {
        value: parameter.documentation,
        isTrusted: true,
      },
    })),
  };

  return {
    signatures: [signature],
    activeSignature: 0,
    activeParameter: numberOfCommas,
  };
};

const sanitizeRegExpParameter = (regExpParameter: string) => {
  // Adapted from https://stackoverflow.com/a/17886301
  return regExpParameter.replace(/[-\\/\\^$*+?.()|[\]{}]/g, "\\$&");
};

export { builtInFunctionNames, generateFunctionsAutocompleteSuggestions, generateFunctionsSignatureHelp };
